import { SearchModelEnum } from "types/qbank/searchModel";
import { AutoSuggestValue } from "types/autoSuggestValue";
import { SearchFilterLabels } from "types/searchFilterLabels";
import { SearchOperator } from "types/qbank/searchOperator";
import {
  MetaDataPropertyType,
  MetaDataPropertyTypes,
} from "types/metaDataPropertyTypes";

const accentMap: { [key: string]: string } = {
  Š: "S",
  š: "s",
  Ž: "Z",
  ž: "z",
  À: "A",
  Á: "A",
  Â: "A",
  Ã: "A",
  Ä: "A",
  Å: "A",
  Æ: "A",
  Ç: "C",
  È: "E",
  É: "E",
  Ê: "E",
  Ë: "E",
  Ì: "I",
  Í: "I",
  Î: "I",
  Ï: "I",
  Ñ: "N",
  Ò: "O",
  Ó: "O",
  Ô: "O",
  Õ: "O",
  Ö: "O",
  Ø: "O",
  Ù: "U",
  Ú: "U",
  Û: "U",
  Ü: "U",
  Ý: "Y",
  Þ: "B",
  ß: "Ss",
  à: "a",
  á: "a",
  â: "a",
  ã: "a",
  ä: "a",
  å: "a",
  æ: "a",
  ç: "c",
  è: "e",
  é: "e",
  ê: "e",
  ë: "e",
  ì: "i",
  í: "i",
  î: "i",
  ï: "i",
  ð: "o",
  ñ: "n",
  ò: "o",
  ó: "o",
  ô: "o",
  õ: "o",
  ö: "o",
  ø: "o",
  ù: "u",
  ú: "u",
  û: "u",
  ý: "y",
  þ: "b",
  ÿ: "y",
};

export function normalize(term: string): string {
  let ret = "";
  for (let i = 0, l = term.length; i < l; i++) {
    ret += accentMap[term.charAt(i)] || term.charAt(i);
  }
  return ret.toLowerCase();
}

export function loadAutoSuggestValues(metaData: {
  [key: string]: any;
}): AutoSuggestValue[] {
  let values: AutoSuggestValue[] = [];

  if (metaData.categories) {
    const categories: {
      [key: number]: {
        id: number;
        name: string;
      };
    } = metaData.categories;
    Object.entries(categories).forEach((category) => {
      values.push({
        searchCriteria: SearchModelEnum.categoryIds,
        label: SearchFilterLabels.categoryIds,
        labelValue: category[1].name,
        value: category[1].id,
        normalizedValue: normalize(category[1].name),
      });
    });
  }

  if (metaData.users) {
    const users: {
      [key: number]: {
        id: number;
        name: string;
      };
    } = metaData.users;
    Object.entries(users).forEach((user) => {
      values.push({
        searchCriteria: SearchModelEnum.uploadedByIds,
        label: SearchFilterLabels.uploadedByIds,
        labelValue: user[1].name,
        value: user[1].id,
        normalizedValue: normalize(user[1].name),
      });
    });
  }

  if (metaData.extensions) {
    const extensions: { [key: string]: string[] } = metaData.extensions;
    Object.entries(extensions).forEach((extension) => {
      if (["audio", "image", "video"].indexOf(extension[0]) > -1) {
        //add wildcard search options for these categories
        //the other categories of file extensions aren't all prefixed in the same way
        values.push({
          searchCriteria: SearchModelEnum.mimeTypes,
          label: SearchFilterLabels.mimeTypes,
          labelValue: extension[0] + "/%",
          value: extension[0] + "/%",
          normalizedValue: normalize(extension[0] + "/%"),
        });
      }

      extension[1].forEach((value) => {
        values.push({
          searchCriteria: SearchModelEnum.mimeTypes,
          label: SearchFilterLabels.mimeTypes,
          labelValue: value,
          value: value,
          normalizedValue: normalize(extension[0] + "/%"),
        });
      });
    });
  }

  if (metaData.propertyTypes) {
    const propertyTypes: MetaDataPropertyTypes = metaData.propertyTypes;

    function parseStructure(
      propertyType: MetaDataPropertyType,
      structure: { [key: string]: { [key: string]: string } | string },
      path?: string[]
    ) {
      Object.entries(structure).forEach((node) => {
        if (typeof node[1] === "object") {
          parseStructure(propertyType, node[1], [...(path ?? []), node[0]]);
        } else {
          const localPath = [...(path ?? []), node[0]];
          values.push({
            searchCriteria: SearchModelEnum.properties,
            propertyCriteria: {
              id: propertyType.id,
              systemName: propertyType.systemname,
              operator: SearchOperator.HIERARCHICAL_ANY,
              value: [localPath],
            },
            label: propertyType.name,
            labelValue: localPath.join(" >> "),
            value: node[0],
            normalizedValue: normalize(localPath.join(" ")),
          });
        }
      });
    }

    Object.entries(propertyTypes).forEach((propertyType) => {
      if (!propertyType[1].definition.ignoreInSuggestions) {
        if (propertyType[1].definition.hierarchical) {
          const structure =
            JSON.parse(propertyType[1].definition.structure ?? "{}") ?? {};
          parseStructure(propertyType[1], structure);
        } else if (
          propertyType[1].definition.array &&
          propertyType[1].definition.options?.length
        ) {
          for (
            let i = 0, l = propertyType[1].definition.options.length;
            i < l;
            i++
          ) {
            values.push({
              searchCriteria: SearchModelEnum.properties,
              propertyCriteria: {
                id: propertyType[1].id,
                systemName: propertyType[1].systemname,
                operator: SearchOperator.CONTAINS_ANY,
                value: propertyType[1].definition.options[i],
              },
              label: propertyType[1].name,
              labelValue: propertyType[1].definition.options[i].toString(),
              value: propertyType[1].definition.options[i],
              normalizedValue: normalize(
                propertyType[1].definition.options[i].toString()
              ),
            });
          }
        }
      }
    });
  }

  return values;
}
